import { Tooltip } from '@app/components/tooltip';
import { getExportTrackingTab } from '@app/features/analytics/analytics-tracking';
import { useAnalytics } from '@app/features/analytics/hooks/use-analytics';
import { CloudDownloadIcon } from '@app/features/analytics/icons/cloud-download';
import { useFlags } from '@app/hooks/use-flags';
import { useReadDashboardSharedState } from '@hopin-team/dashboard-shared-react';
import { useTranslation } from '@hopin-team/provider-translation';
import { Spinner } from '@hopin-team/ui-spinner';
import React, { useState } from 'react';
import { toast } from 'react-hot-toast';

import { Button } from '../../components';
import {
  FLAG_ENABLE_ANALYTICS_LEAD_FILTER,
  FLAG_ENABLE_CH_DASHBOARD_ANALYTICS,
} from '../../constants';
import { useAuthentication } from '../../hooks/use-authentication';
import { useSpaceContext } from '../../hooks/use-space-context';
import { useAnalyticsContext } from '../../providers/analytics-provider';
import {
  exportAdminAnalyticsReport,
  generateReport,
} from '../../services/endpoints';
import { buildQuery, getTitleCase } from '../../utils';

export type ExportableModule =
  | 'events'
  | 'registrants'
  | 'videos'
  | 'series'
  | 'admin';

interface ExportProps {
  [key: string]: string | string[] | boolean;
}

const MODULE_TO_FILENAME: Record<ExportableModule, string> = {
  events: 'Events',
  registrants: 'Contacts',
  videos: 'Videos',
  series: 'Series',
  admin: 'Admin',
} as const;

const INITIAL_EXPORTING_STATE: Record<ExportableModule, boolean> = {
  events: false,
  registrants: false,
  videos: false,
  series: false,
  admin: false,
} as const;

const DEFAULT_EXPORT_MIME_TYPE = 'text/csv;charset=utf-8;';

const isExportableModule = (value: string): value is ExportableModule =>
  Object.prototype.hasOwnProperty.call(MODULE_TO_FILENAME, value);

const getExportBlob = (data: Blob | string): Blob =>
  data instanceof Blob
    ? data
    : new Blob([data], { type: DEFAULT_EXPORT_MIME_TYPE });

const getExportFilename = (
  module: ExportableModule,
  blob: Blob,
): `${string}.${'csv' | 'zip'}` => {
  const extension =
    module === 'admin' || blob.type.toLowerCase().includes('zip')
      ? 'zip'
      : 'csv';

  return `${MODULE_TO_FILENAME[module]}.${extension}`;
};

export const Export = ({
  customProps,
}: {
  customProps?: Partial<ExportProps>;
}) => {
  const { t } = useTranslation();
  const [isExporting, setIsExporting] = useState<
    Record<ExportableModule, boolean>
  >(INITIAL_EXPORTING_STATE);
  const { state } = useAnalyticsContext();
  const {
    dateRange,
    startDate,
    endDate,
    eventTypes,
    eventSortName,
    eventSortDirection,
    registrantSortName,
    registrantSortDirection,
    videoSortName,
    videoSortDirection,
    searchText,
    selectedVideos,
    selectedEvents,
    selectedTab,
  } = state;
  const flags = useFlags();
  const organizationExternalId =
    useReadDashboardSharedState('org_id')?.toString() || '';
  const { isLoading: isAuthLoading, token } = useAuthentication();
  const { spaceExternalId } = useSpaceContext();

  const enableCHDashboardAnalytics =
    flags?.[FLAG_ENABLE_CH_DASHBOARD_ANALYTICS];
  const enableLeadFilter = flags?.[FLAG_ENABLE_ANALYTICS_LEAD_FILTER];
  const isAdminExportDisabled =
    selectedTab === 'admin' && (isAuthLoading || !token);

  const { track } = useAnalytics();
  const exportTooltip = t('analytics.export-report-tooltip');

  const onExport = async () => {
    if (!isExportableModule(selectedTab) || isAdminExportDisabled) {
      return;
    }

    track('ola_export_click', {
      tabName: getTitleCase(selectedTab),
      tab: getExportTrackingTab({
        selectedTab,
        enableCHDashboardAnalytics,
      }),
    });
    setIsExporting(prev => ({ ...prev, [selectedTab]: true }));

    const config = {
      dateRange,
      startDate,
      endDate,
      eventTypes,
      ...(enableLeadFilter && {
        contactType: state.contactType,
      }),
      name: (
        {
          events: eventSortName,
          registrants: registrantSortName,
          videos: videoSortName,
          series: '', // Series sorting feature is not implemented yet
          admin: '',
        } satisfies Record<ExportableModule, string>
      )[selectedTab],
      direction: (
        {
          events: eventSortDirection,
          registrants: registrantSortDirection,
          videos: videoSortDirection,
          series: '', // Series sorting feature is not implemented yet
          admin: '',
        } satisfies Record<ExportableModule, string>
      )[selectedTab],
      searchText,
      ...(selectedTab !== 'videos' && { selectedEvents }),
      ...(enableCHDashboardAnalytics &&
        selectedTab === 'registrants' && {
          includeContacts: true,
          includeUpcoming: true,
          eventNames: [...(selectedVideos || [])],
        }),
      spaceExternalId,
      ...(customProps || {}),
    };
    const query =
      selectedTab === 'admin'
        ? (() => {
            const adminParams = new URLSearchParams(
              buildQuery({ dateRange, startDate, endDate }),
            );
            if (spaceExternalId)
              adminParams.set('space_external_id', spaceExternalId);
            return adminParams.toString();
          })()
        : buildQuery({ ...config });

    try {
      const data =
        selectedTab === 'admin'
          ? await exportAdminAnalyticsReport(organizationExternalId, query)
          : await generateReport(selectedTab, query);

      const blob = getExportBlob(data);

      // Create a link element to trigger the download
      const link = document.createElement('a');
      const url = window.URL.createObjectURL(blob);

      link.href = url;
      link.setAttribute('download', getExportFilename(selectedTab, blob));
      document.body.appendChild(link);
      link.click(); // Trigger the download

      // Clean up the link after the download
      link.parentNode?.removeChild(link);
      window.URL.revokeObjectURL(url); // Release the object URL after download
    } catch (error) {
      toast.error('analytics.error-message');
    } finally {
      setIsExporting(prev => ({ ...prev, [selectedTab]: false }));
    }
  };

  return (
    <>
      {isExportableModule(selectedTab) && isExporting[selectedTab] ? (
        <Button
          size="medium"
          kind="secondary"
          pattern="outline"
          className="!px-2 border-neutral-b4 !rounded-lg outline-none"
          data-testid="generating-button"
        >
          <div className="flex h-10 items-center gap-2">
            <Spinner isPolite isShowing theme="secondary" />
            <div>{t('analytics.generating')}</div>
          </div>
        </Button>
      ) : (
        <Tooltip tooltip={exportTooltip} placement="bottom-center">
          <Button
            onClick={onExport}
            pattern="outline"
            kind="secondary"
            size="medium"
            disabled={isAdminExportDisabled}
            aria-label={exportTooltip}
            data-testid="export-button"
            className="!px-2 border-neutral-b4 !rounded-lg outline-none focus-visible:border-blue-500 focus-visible:outline focus-visible:ring-1 focus-visible:outline-1 focus-visible:outline-blue-500 focus-visible:outline-offset-0"
          >
            <CloudDownloadIcon />
          </Button>
        </Tooltip>
      )}
    </>
  );
};
